import classNames from 'classnames';
import noop from 'lodash/noop';
import React, { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { defineWithDefaults } from 'rodash';
import { compareTwoStrings } from 'string-similarity';
import '../../styles/search-select-field.scss';
import { DefaultFieldRenderer, RawFieldRenderer } from '../field-renderers';
import useFocusGroup from '../hooks/useFocusGroup';
import { FieldProps, JsxSelectOption, SelectOption, SelectProps, SelectValue } from '../types';
import InputTextField from './InputTextField';
import ListSelectField from './ListSelectField';

type Props<Value extends SelectValue> =
    FieldProps<Value, HTMLInputElement> & ExtraProps<Value>;

const withDefaults = defineWithDefaults<Props<SelectValue>>({
    Renderer: DefaultFieldRenderer,
    className: '',
    maxResults: 20,
    name: null,
    onBlur: noop,
    onBlurCapture: noop,
    onFocus: noop,
    onFocusCapture: noop,
    onKeyDown: noop,
    onValueChange: noop,
    onValueReset: noop,
    onValueSubmit: noop,
    options: [],
    value: null,
});

interface ExtraProps<Value extends SelectValue> extends SelectProps<Value> {
    /**
     * Maximum number of results that are allowed to display
     */
    maxResults?: number;
    placeholder?: string;
}

const SearchSelectField = <Value extends SelectValue = string>(
    props: Props<Value>
) => {
    props = withDefaults(props);

    const [isListOpen, setIsListOpen] = useState(false);

    const invalidOption = useMemo<SelectOption<Value>>(
        () => ({ value: props.invalidValue, label: props.invalidLabel }),
        [props.invalidValue, props.invalidLabel]
    );

    const getOptionByValue = useCallback(
        (v: Value) => props.options.find((o) => o.value === v) || invalidOption,
        [props.options]
    );

    const getOptionByLabel = useCallback(
        (l: string) =>
            props.options.find((o) => o.label === l) || invalidOption,
        [props.options]
    );

    const [search, setSearch] = useState(getOptionByValue(props.value)?.label);

    const [results, setResults] = useState<Array<JsxSelectOption<Value>>>([]);

    const updateSearch = useCallback((v: Value) => {
        const opt = getOptionByValue(v);
        if (opt.value === props.invalidValue) {
            return;
        }
        setSearch(opt.label);
    }, [getOptionByValue, props.invalidValue]);

    const updateResults = useCallback(
        () => setResults(
            props.options.map((option) => ({
                option,
                w: compareTwoStrings(
                    search ? search.toLowerCase() : '',
                    option.label.toLowerCase()
                ),
            }))
            .sort((a, b) => b.w - a.w)
            .filter(({w}) => w)
            .slice(0, props.maxResults)
            .map(
                ({option: {value: v, label}}) => ({
                    label: highlightMatchingCharacters(label, search || ''),
                    value: v,
                })
            )
        )
    , [search, props.options, props.maxResults]);

    const isOpen = useMemo(
        () => isListOpen && results?.length > 0,
        [isListOpen, results?.length]
    );

    const currentOption = useMemo(
        () => getOptionByLabel(search),
        [getOptionByLabel, search]
    );

    const error = useMemo(
        () => (
            props.error || (
                !isListOpen &&
                currentOption.value === props.invalidValue
            )
        ),
        [props.error, isListOpen, currentOption, props.invalidValue]
    );

    const getResult = useCallback(
        (v: Value) => results.find((r) => r.value === v),
        [results]
    );

    const selectedResult = useMemo(
        () => getResult(currentOption.value),
        [getResult, currentOption.value]
    );

    const handleListValueChange = useCallback((
        v: Value,
        _: Value,
        __: string
    ) => {
        props.onValueChange(v, props.value, props.name);
        setIsListOpen(false);
        updateSearch(v);
    }, [
        getOptionByValue,
        props.name,
        props.onValueChange,
        props.value,
        props.invalidValue,
    ]);

    const handleSearchKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
        props.onKeyDown(e);
        if (e.isDefaultPrevented()) {
            return;
        }
        if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
            e.preventDefault();
            if (!isListOpen) {
                setIsListOpen(true);
                return;
            }
            const i = results.indexOf(selectedResult);
            const iNext = (
                (e.key === 'ArrowDown') ?
                    Math.min(i + 1, results.length - 1) :
                (e.key === 'ArrowUp') ?
                    Math.max(i - 1, 0) :
                null
            );
            const next = results[iNext];
            setSearch(getOptionByValue(next.value).label);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (isListOpen) {
                handleListValueChange(currentOption.value, null, null);
            } else {
                props.onValueSubmit(currentOption.value, props.name);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            if (isListOpen) {
                setIsListOpen(false);
            } else {
                props.onValueReset(currentOption.value, props.name);
            }
        } else if (!(['ArrowLeft', 'ArrowRight'].includes(e.key))) {
            updateResults();
            setIsListOpen(true);
        }
    }, [
        getOptionByValue,
        handleListValueChange,
        isListOpen,
        props.name,
        props.onKeyDown,
        props.onValueReset,
        props.onValueSubmit,
        results,
        updateResults,
    ]);

    const { handleChildFocus, handleChildBlur } = useFocusGroup({
        onBlur: useCallback(() => {
            props.onBlurCapture();
            setIsListOpen(false);
            if (currentOption && currentOption.value !== props.value) {
                handleListValueChange(currentOption.value, null, null);
            }
        }, [props.onBlur, currentOption, props.value, handleListValueChange]),

        onFocus: useCallback(() => {
            updateResults();
            setIsListOpen(true);
            props.onFocus();
        }, [updateResults, props.onFocus]),
    });

    useEffect(() => {
        updateResults();
        updateSearch(props.value);
    }, [props.value]);

    const factory = useCallback(({
        className,

        // Unused props
        Renderer, label, error: __, maxResults, onValueChange, options,
        onValueSubmit, onValueReset, onKeyDown, value: _,
        invalidValue, invalidLabel,

        ...otherProps
    }) => <div className={classNames(
            'search-select-field',
            className,
            { open: isOpen }
        )}>
                <InputTextField {...otherProps}
                    Renderer={RawFieldRenderer}
                    onInputChange={setSearch}
                    onKeyDown={handleSearchKeyDown}
                    value={search}
                    onFocus={handleChildFocus}
                    onBlur={handleChildBlur}
                />
            {isOpen &&
                <ListSelectField Renderer={RawFieldRenderer}
                    onValueChange={handleListValueChange}
                    options={results}
                    value={currentOption.value}
                    onFocus={handleChildFocus}
                    onBlur={handleChildBlur}
                />}
        </div>, [
        handleSearchKeyDown,
        handleListValueChange,
        isOpen,
        results,
        search,
    ]);

    return <props.Renderer
        {...props}
        error={error}
        factory={factory}
    />;
};

function highlightMatchingCharacters(label: string, search: string) {
    const searchL = search.toLowerCase();
    let next = 0;
    const processedChars: Array<string|JSX.Element> = [];
    for (const lc of label.toLowerCase()) {
        if (!lc) {
            continue;
        }
        if (lc === searchL[next]) {
            processedChars.push(<strong>{lc}</strong>);
            next++;
        } else {
            processedChars.push(lc);
        }
    }
    return <>{processedChars}</>;
}

export default SearchSelectField;
